import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Home, MapPin, DollarSign, Search, Grid, List, ChevronLeft, ChevronRight, ChevronDown, CheckCircle } from 'lucide-react';
import { useGetPublicSoldListingsQuery } from '../store/slices/apiSlice';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

const PublicSoldListings: React.FC = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<'all' | 'car' | 'house'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, searchTerm, itemsPerPage]);

  const { data: listingsData, isLoading } = useGetPublicSoldListingsQuery({
    listing_type: selectedType === 'all' ? 'all' : selectedType,
    page: currentPage,
    limit: itemsPerPage,
  });

  const listings = listingsData?.data?.listings || [];
  const pagination = listingsData?.data?.pagination || {
    current_page: 1,
    total_pages: 1,
    total_records: 0,
  };

  // Client-side search filtering
  const filteredListings = listings.filter((listing: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      listing.title?.toLowerCase().includes(searchLower) ||
      listing.description?.toLowerCase().includes(searchLower) ||
      listing.location?.toLowerCase().includes(searchLower)
    );
  });

  const formatCurrency = (amount: number, symbol: string | null = null) => {
    const currencySymbol = symbol || '$';
    return `${currencySymbol}${amount.toLocaleString('en-US')}`;
  };

  const getPrimaryImage = (images: any[]) => {
    if (!images || images.length === 0) {
      return 'https://via.placeholder.com/400x300?text=No+Image';
    }
    const primaryImage = images.find((img) => img.is_primary);
    return primaryImage?.image_path || images[0]?.image_path || 'https://via.placeholder.com/400x300?text=No+Image';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Sold Items</h1>
              <p className="text-gray-600">Browse recently sold cars and houses</p>
            </div>
            
            {/* Login Button */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  if (window.innerWidth < 1024) {
                    window.location.href = 'https://app.diasporawise.com';
                  } else {
                    navigate('/');
                  }
                }}
                variant="outline"
                className="flex items-center gap-2 lg:hidden"
              >
                Login to View Marketplace
              </Button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 space-y-4">
          {/* Top Row: Search, Type Filter, View Toggle */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, description, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />
            </div>

            {/* Type Filter */}
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedType('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedType === 'all'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedType('car')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  selectedType === 'car'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Car className="w-4 h-4" />
                Cars
              </button>
              <button
                onClick={() => setSelectedType('house')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  selectedType === 'house'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Home className="w-4 h-4" />
                Houses
              </button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2 border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count and Per Page Selection */}
        <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm text-gray-600">
            Showing {filteredListings.length} of {pagination.total_records} {pagination.total_records === 1 ? 'sold item' : 'sold items'}
            {pagination.total_pages > 1 && ` (Page ${pagination.current_page} of ${pagination.total_pages})`}
          </p>
          
          {/* Per Page Selection */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 font-medium">Items per page:</label>
            <div className="relative">
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-gray-500 focus:border-transparent cursor-pointer"
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={36}>36</option>
                <option value={48}>48</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Listings Grid/List */}
        {filteredListings.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-gray-400 mb-4">
              {selectedType === 'car' ? (
                <Car className="w-16 h-16 mx-auto" />
              ) : selectedType === 'house' ? (
                <Home className="w-16 h-16 mx-auto" />
              ) : (
                <CheckCircle className="w-16 h-16 mx-auto" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Sold Items Found</h3>
            <p className="text-gray-600">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : 'No sold items available at the moment'}
            </p>
          </Card>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {filteredListings.map((listing: any) => (
              <Card key={listing.listing_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Image */}
                <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200">
                  <img
                    src={getPrimaryImage(listing.images)}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                  <div className="absolute top-3 left-3">
                    <Badge
                      variant={listing.listing_type === 'car' ? 'secondary' : 'outline'}
                      className="bg-white text-gray-900"
                    >
                      {listing.listing_type === 'car' ? (
                        <Car className="w-3 h-3 inline mr-1" />
                      ) : (
                        <Home className="w-3 h-3 inline mr-1" />
                      )}
                      {listing.listing_type === 'car' ? 'Car' : 'House'}
                    </Badge>
                  </div>
                  <div className="absolute top-3 right-3">
                    <Badge variant="success" className="bg-green-600 text-white flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Sold
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {listing.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {listing.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    {listing.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span className="truncate">{listing.location}</span>
                      </div>
                    )}
                    <div className="flex items-center text-lg font-bold text-gray-900">
                      <DollarSign className="w-5 h-5 mr-1" />
                      {formatCurrency(listing.price, listing.currency_symbol)}
                    </div>
                  </div>

                  {/* View Details Button - Links to marketplace detail page */}
                  <Link to={`/marketplace/${listing.listing_id}`}>
                    <button className="w-full bg-gray-800 text-white py-2 px-4 rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium">
                      View Details
                    </button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.min(pagination.total_pages, prev + 1))}
              disabled={currentPage === pagination.total_pages}
              className="flex items-center gap-1"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PublicSoldListings;
